import UserProfile, { UserProfileSettings } from "../models/user/UserProfile";
import {
  EXPENSES_TRACKER_USER_PROFILE_URL,
  USER_PASSWORD_URL,
  USER_EMAIL_CHANGE_URL,
  USER_EMAIL_CHANGE_CANCEL_URL,
  USER_EMAIL_VERIFY_URL,
  USER_EMAIL_CHANGE_COMPLETE_URL,
  USER_EMAIL_CHANGE_STATUS_URL,
  EXPENSES_TRACKER_USER_PROFILE_SETTINGS_URL,
} from "../utils/constants/ApiConstants";
import { httpClient } from "./http/httpClient";
import { UserProfileSchema, UserProfileSettingsSchema, validate } from "./schemas";

export async function getUserProfile(): Promise<UserProfile> {
  const { data } = await httpClient.get<UserProfile>(EXPENSES_TRACKER_USER_PROFILE_URL, { authenticated: true });
  return validate(UserProfileSchema, data);
}

export async function getUserProfileSettings(): Promise<UserProfileSettings> {
  const { data } = await httpClient.get<UserProfileSettings>(EXPENSES_TRACKER_USER_PROFILE_SETTINGS_URL, { authenticated: true });
  return validate(UserProfileSettingsSchema, data);
}

export async function updateUserProfile(newUserProfile: UserProfile): Promise<UserProfile> {
  const { data } = await httpClient.put<UserProfile, UserProfile>(EXPENSES_TRACKER_USER_PROFILE_URL, newUserProfile, {
    authenticated: true,
  });
  return validate(UserProfileSchema, data);
}

export async function updateUserProfileSettings(newUserProfileSettings: UserProfileSettings): Promise<UserProfileSettings> {
  const { data } = await httpClient.put<UserProfileSettings, UserProfileSettings>(EXPENSES_TRACKER_USER_PROFILE_SETTINGS_URL, newUserProfileSettings, { authenticated: true });
  return validate(UserProfileSettingsSchema, data);
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await httpClient.put<void, { currentPassword: string; newPassword: string }>(
    USER_PASSWORD_URL,
    { currentPassword, newPassword },
    { authenticated: true }
  );
}

export async function initiateEmailChange(newEmail: string, currentPassword: string, verificationCallbackUrl: string):
  Promise<{ message: string; pendingEmail: string }> {
  const payload = { newEmail, currentPassword, verificationCallbackUrl };
  const { data } = await httpClient.post<{ message: string; pendingEmail: string }, typeof payload>(
    USER_EMAIL_CHANGE_URL,
    payload,
    { authenticated: true }
  );
  return data;
}

export async function cancelEmailChange(): Promise<void> {
  await httpClient.delete<void>(USER_EMAIL_CHANGE_CANCEL_URL, { authenticated: true });
}

export async function verifyEmailChange(token: string, type: "old" | "new"):
  Promise<{ message: string; oldEmailVerified: boolean; newEmailVerified: boolean; changeCompleted: boolean }> {
  const query = { token, type };
  const { data } = await httpClient.public.get<
    { message: string; oldEmailVerified: boolean; newEmailVerified: boolean; changeCompleted: boolean }
  >(USER_EMAIL_VERIFY_URL, { query });
  return data;
}

export async function completeEmailChange(): Promise<{ message: string; changeCompleted: boolean }> {
  const { data } = await httpClient.post<{ message: string; changeCompleted: boolean }, {}>(
    USER_EMAIL_CHANGE_COMPLETE_URL,
    {},
    { authenticated: true }
  );
  return data;
}

export async function getEmailChangeStatus(): Promise<{
  hasPendingChange: boolean;
  pendingEmail?: string;
  oldEmailVerified: boolean;
  newEmailVerified: boolean;
  canComplete: boolean;
}> {
  const { data } = await httpClient.get<{
    hasPendingChange: boolean;
    pendingEmail?: string;
    oldEmailVerified: boolean;
    newEmailVerified: boolean;
    canComplete: boolean;
  }>(USER_EMAIL_CHANGE_STATUS_URL, { authenticated: true });
  return data;
}


